import { ComponentType, lazy } from 'react'
import { createBrowserRouter, redirect, RouteObject } from 'react-router-dom'
import { langTypes } from '@/store/language/language'
import { isApplication } from '@/utils/app'
import withLoadedNotify from './pages/utils/withLoadedNotify/withLoadedNotify'
import withErrorBoundary from '@/Routes/pages/utils/withErrorBoundary/withErrorBoundary'
import catchImportComponentError from '@/Routes/pages/utils/catchImportModule/catchImportComponentError'
import RootRoute from './pages/RootRoute/RootRoute'

const lazyComponent = <T extends ComponentType<any>>(func: () => Promise<{default: T}>) => {
  return async() => ({
    Component: lazy(catchImportComponentError(async() => {
      const { default: Component } = await func()
      return { default: withErrorBoundary(withLoadedNotify(Component)) }
    }))
  })
}

const isApp = isApplication()

// The app (chat) entry, also reused as the mobile catch-all: it already handles
// the redirect logic (logged-in → app, otherwise → login on mobile / landing on web).
const mainRoute: RouteObject = {
  index: true,
  lazy: lazyComponent(() => import('./pages/MainRoute/MainRoute'))
}

const loginRoute: RouteObject = {
  path: 'login',
  lazy: lazyComponent(() => import('./pages/LoginRoute/LoginRoute'))
}

// Available on both platforms: the root route sends every address carrying SSO
// credentials here, whatever page it was.
const ssoRoute: RouteObject = {
  path: 'sso',
  lazy: lazyComponent(() => import('@/pages/Sso/Sso'))
}

// In the mobile app only `/` (MainRoute) and `/login` exist; everything else is
// sent to NotFoundRoute, which redirects to the root or login depending on auth.
const notFoundRoute: RouteObject = {
  path: '*',
  lazy: lazyComponent(() => import('./pages/NotFoundRoute/NotFoundRoute'))
}

const mobileChildren: RouteObject[] = [
  mainRoute,
  loginRoute,
  notFoundRoute
]

// On desktop all landing pages are available under the shared landing layout,
// and login is a separate landing-styled page inside that layout.
const desktopChildren: RouteObject[] = [
  mainRoute,
  {
    lazy: lazyComponent(() => import('./pages/LandingLayoutRoute/LandingLayoutRoute')),
    children: [
      {
        path: 'login',
        lazy: lazyComponent(() => import('./pages/LandingLoginRoute/LandingLoginRoute'))
      },
      {
        path: 'signup',
        lazy: lazyComponent(() => import('./pages/SignupRoute/SignupRoute'))
      },
      {
        path: 'forgot-password',
        lazy: lazyComponent(() => import('./pages/ForgotPasswordRoute/ForgotPasswordRoute'))
      },
      {
        path: 'reset-password',
        lazy: lazyComponent(() => import('@/pages/landing/ResetPassword/ResetPassword'))
      },
      {
        path: 'main',
        lazy: lazyComponent(() => import('@/pages/landing/HomeLanding/HomeLanding'))
      },
      {
        path: 'pricing',
        lazy: lazyComponent(() => import('@/pages/landing/Pricing/Pricing'))
      },
      {
        path: 'checkout',
        lazy: lazyComponent(() => import('./pages/CheckoutRoute/CheckoutRoute'))
      },
      {
        path: 'checkout/result',
        lazy: lazyComponent(() => import('@/pages/landing/CheckoutResult/CheckoutResult'))
      },
      {
        path: 'purchases',
        lazy: lazyComponent(() => import('./pages/PurchasesRoute/PurchasesRoute'))
      },
      {
        path: 'account',
        lazy: lazyComponent(() => import('./pages/AccountDetailsRoute/AccountDetailsRoute'))
      },
      {
        path: 'download',
        lazy: lazyComponent(() => import('@/pages/landing/Download/Download'))
      },
      {
        path: 'about',
        lazy: lazyComponent(() => import('@/pages/landing/About/About'))
      },
      {
        path: 'contact',
        lazy: lazyComponent(() => import('@/pages/landing/Contact/Contact'))
      },
      {
        path: 'privacy',
        lazy: lazyComponent(() => import('@/pages/landing/PrivacyPolicy/PrivacyPolicy'))
      },
      {
        path: 'terms',
        lazy: lazyComponent(() => import('@/pages/landing/TermsOfUse/TermsOfUse'))
      },
      {
        path: 'refund',
        lazy: lazyComponent(() => import('@/pages/landing/RefundPolicy/RefundPolicy'))
      }
    ]
  },
  notFoundRoute
]

// Dev-only: expose the mobile app login at /mobile-login so it can be previewed
// in a regular browser (outside a WebView).
const devChildren: RouteObject[] = import.meta.env.DEV
  ? [
    {
      path: 'mobile-login',
      lazy: lazyComponent(() => import('./pages/LoginRoute/LoginRoute'))
    }
  ]
  : []

const router = createBrowserRouter([
  {
    path: '/:langParam?',
    Component: RootRoute,
    loader: async({ params, request }) => {
      const { langParam } = params
      if (langParam && !(langParam in langTypes)) {
        const url = new URL(request.url)
        const search = url.search
        return redirect(`/${search}`)
      }
      return null
    },
    children: [ssoRoute, ...(isApp ? mobileChildren : desktopChildren), ...devChildren]
  }
])

export default router
